import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import VoiceTextInput from '../components/VoiceTextInput';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import EmergencyButton from '../components/EmergencyButton';
import { authFetch } from '../lib/authFetch';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function AdmissionAssessmentPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});

  const toggleCheckbox = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admission-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Assessment completed');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 pb-20">
      <header className="fixed top-0 left-0 right-0 bg-gradient-to-r from-blue-700 to-blue-800 border-b-4 border-blue-900 z-40 h-16 px-4 flex items-center justify-between shadow-xl">
        <button onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <img src={AXIOM_LOGO} alt="Axiom" className="h-8" />
        <EmergencyButton />
      </header>

      <main className="pt-20 px-4 pb-6">
        <div className="max-w-4xl mx-auto">
          
          {/* HEADER */}
          <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-t-xl p-6 shadow-2xl border-4 border-blue-900">
            <h1 className="text-3xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Admission Assessment / Interactive Treatment Plan
            </h1>
            <p className="text-blue-100 text-center text-sm">2-Week Window Requirement</p>
          </div>

          <div className="bg-white shadow-2xl">
            
            {/* 1. ACCOMMODATION */}
            <div className="bg-gradient-to-r from-blue-300 to-blue-400 p-6 border-b-4 border-blue-500">
              <h2 className="text-xl font-bold text-blue-900 mb-4">1. ACCOMMODATION</h2>
              
              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="font-semibold text-blue-900 mb-3">Institution</p>
                <div className="flex items-center space-x-2">
                  <Checkbox checked={formData.acc_institution} onCheckedChange={() => toggleCheckbox('acc_institution')} id="acc-inst" />
                  <Label htmlFor="acc-inst" className="text-lg">☑ Yes</Label>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="font-semibold text-blue-900 mb-3">When Not in Prison</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={formData.acc_own} onCheckedChange={() => toggleCheckbox('acc_own')} id="acc-own" />
                    <Label htmlFor="acc-own">Own Premises</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={formData.acc_room} onCheckedChange={() => toggleCheckbox('acc_room')} id="acc-room" />
                    <Label htmlFor="acc-room">Room/Boarding</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={formData.acc_nfa} onCheckedChange={() => toggleCheckbox('acc_nfa')} id="acc-nfa" />
                    <Label htmlFor="acc-nfa">No Fixed Address</Label>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="font-semibold text-blue-900 mb-3">Was Accommodation</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={formData.acc_safe} onCheckedChange={() => toggleCheckbox('acc_safe')} id="safe" />
                    <Label htmlFor="safe">Safe/Supportive</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={formData.acc_unsafe} onCheckedChange={() => toggleCheckbox('acc_unsafe')} id="unsafe" />
                    <Label htmlFor="unsafe">Unsafe</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={formData.acc_drug} onCheckedChange={() => toggleCheckbox('acc_drug')} id="drug" />
                    <Label htmlFor="drug">Drug Friendly</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={formData.acc_violent} onCheckedChange={() => toggleCheckbox('acc_violent')} id="violent" />
                    <Label htmlFor="violent">Violent</Label>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-3">
                <Label className="text-sm font-semibold text-blue-900 mb-2 block">Additional Information?</Label>
                <VoiceTextInput value={formData.acc_notes || ''} onChange={(v) => handleChange('acc_notes', v)} rows={2} className="bg-white" testId="acc-notes" />
              </div>
            </div>

            {/* 2. EDUCATION */}
            <div className="bg-gradient-to-r from-blue-300 to-blue-400 p-6 border-b-4 border-blue-500">
              <h2 className="text-xl font-bold text-blue-900 mb-4">2. EDUCATION</h2>
              
              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="font-semibold text-blue-900 mb-3">Level Completed</p>
                <div className="flex flex-wrap gap-3">
                  {['6', '7', '8', '9', '10', '11', '12'].map(year => (
                    <div key={year} className="flex items-center space-x-2 bg-blue-50 px-3 py-2 rounded">
                      <Checkbox checked={formData[`edu_year${year}`]} onCheckedChange={() => toggleCheckbox(`edu_year${year}`)} id={`year${year}`} />
                      <Label htmlFor={`year${year}`} className="font-semibold">{year}</Label>
                    </div>
                  ))}
                  <div className="flex items-center space-x-2 bg-blue-50 px-3 py-2 rounded">
                    <Checkbox checked={formData.edu_tafe} onCheckedChange={() => toggleCheckbox('edu_tafe')} id="tafe" />
                    <Label htmlFor="tafe">TAFE</Label>
                  </div>
                  <div className="flex items-center space-x-2 bg-blue-50 px-3 py-2 rounded">
                    <Checkbox checked={formData.edu_tertiary} onCheckedChange={() => toggleCheckbox('edu_tertiary')} id="tertiary" />
                    <Label htmlFor="tertiary">Tertiary</Label>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-3">
                <Label className="text-sm font-semibold text-blue-900 mb-2 block">Additional Information?</Label>
                <VoiceTextInput value={formData.edu_notes || ''} onChange={(v) => handleChange('edu_notes', v)} rows={2} className="bg-white" testId="edu-notes" placeholder="Details, courses, qualifications..." />
              </div>
            </div>

            {/* 3. EMPLOYMENT */}
            <div className="bg-gradient-to-r from-blue-300 to-blue-400 p-6 border-b-4 border-blue-500">
              <h2 className="text-xl font-bold text-blue-900 mb-4">3. EMPLOYMENT / INCOME</h2>
              
              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="font-semibold text-blue-900 mb-3">Currently Employed?</p>
                <div className="flex gap-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={formData.employed_yes} onCheckedChange={() => toggleCheckbox('employed_yes')} id="emp-yes" />
                    <Label htmlFor="emp-yes" className="text-lg">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={formData.employed_no} onCheckedChange={() => toggleCheckbox('employed_no')} id="emp-no" />
                    <Label htmlFor="emp-no" className="text-lg">No</Label>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="font-semibold text-blue-900 mb-3">Income Sources</p>
                <div className="grid grid-cols-3 gap-3">
                  {['Prison', 'Student', 'Home Duties', 'Sickness Benefit', 'Pension', 'Retired', 'Crime', 'Borrowing', 'Family Support', 'Welfare Agencies'].map(source => (
                    <div key={source} className="flex items-center space-x-2">
                      <Checkbox checked={formData[`income_${source.toLowerCase().replace(/ /g,'_')}`]} onCheckedChange={() => toggleCheckbox(`income_${source.toLowerCase().replace(/ /g,'_')}`)} id={source} />
                      <Label htmlFor={source} className="text-sm">{source}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-3">
                <Label className="text-sm font-semibold text-blue-900 mb-2 block">Additional Information?</Label>
                <VoiceTextInput value={formData.emp_notes || ''} onChange={(v) => handleChange('emp_notes', v)} rows={2} className="bg-white" testId="emp-notes" placeholder="Employment history, job types..." />
              </div>
            </div>

            {/* 4. CLIENT DRUG PROBLEM - PRINCIPAL */}
            <div className="bg-gradient-to-r from-blue-400 to-blue-500 p-6 border-b-4 border-blue-600">
              <h2 className="text-xl font-bold text-white mb-4">4. CLIENT DRUG PROBLEM - PRINCIPAL</h2>
              
              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="font-semibold text-blue-900 mb-3">Drug Type (Select One)</p>
                <div className="grid grid-cols-3 gap-3">
                  {['Alcohol', 'Opiates', 'Amphetamines', 'Hallucinogens', 'Benzodiazepines', 'Methadone', 'Other Tranquilisers', 'Cannabis', 'Cocaine', 'Ecstasy', 'Steroids', 'Volatile Substances'].map(drug => (
                    <div key={drug} className="flex items-center space-x-2 bg-blue-50 p-2 rounded">
                      <Checkbox checked={formData[`principal_${drug.toLowerCase()}`]} onCheckedChange={() => toggleCheckbox(`principal_${drug.toLowerCase()}`)} id={`prin-${drug}`} />
                      <Label htmlFor={`prin-${drug}`} className="text-sm font-medium">{drug}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-white rounded-lg p-3">
                  <Label className="text-sm font-semibold text-blue-900 mb-2 block">Age First Used</Label>
                  <Input type="number" value={formData.principal_age || ''} onChange={(e) => handleChange('principal_age', e.target.value)} className="bg-blue-50" />
                </div>
                <div className="bg-white rounded-lg p-3">
                  <Label className="text-sm font-semibold text-blue-900 mb-2 block">Period Used (years)</Label>
                  <Input value={formData.principal_period || ''} onChange={(e) => handleChange('principal_period', e.target.value)} className="bg-blue-50" />
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="font-semibold text-blue-900 mb-3">When using this drug do you feel:</p>
                <div className="grid grid-cols-2 gap-3">
                  {['More Tolerance', 'Withdrawn', 'Importance/Priority', 'Compulsion to Use', 'Loss of Control', 'Interferes with Others', 'Unable to Moderate Use', 'Problems With Use'].map(symptom => (
                    <div key={symptom} className="flex items-center space-x-2">
                      <Checkbox checked={formData[`principal_symptom_${symptom.toLowerCase().replace(/ /g,'_')}`]} onCheckedChange={() => toggleCheckbox(`principal_symptom_${symptom.toLowerCase().replace(/ /g,'_')}`)} id={`princsym-${symptom}`} />
                      <Label htmlFor={`princsym-${symptom}`} className="text-sm">{symptom}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-3">
                <Label className="text-sm font-semibold text-blue-900 mb-2 block">Additional Information?</Label>
                <VoiceTextInput value={formData.principal_notes || ''} onChange={(v) => handleChange('principal_notes', v)} rows={2} className="bg-white" testId="principal-notes" placeholder="Frequency, amounts, patterns..." />
              </div>
            </div>

            {/* Continue pattern for all sections... */}
            
            {/* SAVE BUTTONS */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
              <div className="flex gap-4">
                <Button onClick={() => navigate(-1)} variant="outline" className="flex-1 bg-white text-blue-700 hover:bg-blue-50 h-14 text-lg font-bold">
                  Save & Continue Later
                </Button>
                <Button onClick={handleSubmit} className="flex-1 bg-blue-900 text-white hover:bg-blue-950 h-14 text-lg font-bold shadow-xl">
                  Submit Assessment
                </Button>
              </div>
              <p className="text-blue-100 text-center text-sm mt-4">Assessment must be completed during 2-Week Window</p>
            </div>

          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default AdmissionAssessmentPage;
